// Реклама на витрине (этап 8, П1): рекламные позиции под место показа,
// показы и клики рекламных карточек, клики — в localStorage на 14 дней.
//
// Правила те же, что у событий витрины (`discovery.ts`):
//  1. НЕ ТОРМОЗИТЬ ВИТРИНУ. События копятся и уходят пачкой; ничего не ждёт и
//     не бросает. Без шлюза (мок-режим) реклама просто не показывается.
//  2. ПОКАЗ — ЭТО ПОЯВЛЕНИЕ В ОКНЕ ПРОСМОТРА: та же доля видимости (50 %) и
//     выдержка (500 мс), что у показов карточек в discovery.ts. Один показ на
//     кампанию и товар за сессию; шлюз дополнительно считает один показ на
//     посетителя в сутки.
//  3. КЛИК ПОМНИТСЯ 14 ДНЕЙ (окно атрибуции): гость кликнул, через неделю
//     вошёл и купил — после входа витрина присылает идентификатор устройства с
//     токеном, и шлюз привязывает клики устройства к аккаунту. Заказ
//     засчитывается рекламе по последнему клику.
import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";

import { Product } from "@/api";
import { getBuyerAuthToken } from "@/api/token";
import { useProfile, useWishlist } from "@/store";

export type AdPlacement = "catalog" | "search" | "recommendations" | "home" | "email";

export type AdItem = {
	campaignId: string;
	productId: string;
	model: "cpa" | "cpm";
	package: "basic" | "extended" | "maximum" | null;
	product: Product;
};

/** Реклама у карточки: какая кампания и где показана. */
export type AdSlot = { campaignId: string; placement: AdPlacement };

const GATEWAY_URL = process.env.BUN_PUBLIC_API_GATEWAY_URL;

const BATCH_SIZE = 20;
const FLUSH_DELAY_MS = 2_000;
// Те же порог и выдержка, что у показов в discovery.ts.
const IMPRESSION_RATIO = 0.5;
const IMPRESSION_DWELL_MS = 500;
/** Окно атрибуции на шлюзе — 14 дней по умолчанию; столько и храним клики. */
export const AD_CLICK_TTL_MS = 14 * 86_400_000;

const SEEN_KEY = "osmix-ad-seen";
export const CLICKS_KEY = "osmix-ad-clicks";
const SYNCED_KEY = "osmix-ad-synced";

const isBrowser = (): boolean => typeof window !== "undefined";

type AdEvent = { campaignId: string; productId: string; kind: "impression" | "click"; placement: AdPlacement };
export type StoredClick = { campaignId: string; productId: string; at: number };

let queue: AdEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let listenersBound = false;
let seen: Set<string> | null = null;

const visitor = (): string | undefined => {
	try {
		return useWishlist.getState().clientId;
	} catch {
		return undefined;
	}
};

const seenSet = (): Set<string> => {
	if (seen) return seen;
	try {
		const raw = isBrowser() ? window.sessionStorage.getItem(SEEN_KEY) : null;
		seen = new Set<string>(raw ? (JSON.parse(raw) as string[]) : []);
	} catch {
		seen = new Set();
	}
	return seen;
};

const rememberSeen = (key: string): void => {
	const set = seenSet();
	set.add(key);
	try {
		window.sessionStorage.setItem(SEEN_KEY, JSON.stringify([...set]));
	} catch {
		// приватный режим — дедупликация останется в пределах вкладки
	}
};

/** Клики за окно атрибуции; протухшие выбрасываются при чтении. */
export const storedClicks = (now = Date.now()): StoredClick[] => {
	if (!isBrowser()) return [];
	try {
		const raw = window.localStorage.getItem(CLICKS_KEY);
		const list = raw ? (JSON.parse(raw) as StoredClick[]) : [];
		return list.filter((c) => c && typeof c.at === "number" && now - c.at < AD_CLICK_TTL_MS);
	} catch {
		return [];
	}
};

export const rememberClick = (click: Omit<StoredClick, "at">, now = Date.now()): void => {
	if (!isBrowser()) return;
	try {
		// Последний клик по товару вытесняет прежний: атрибуция — по последнему.
		const next = [
			...storedClicks(now).filter((c) => c.productId !== click.productId),
			{ ...click, at: now },
		].slice(-50);
		window.localStorage.setItem(CLICKS_KEY, JSON.stringify(next));
	} catch {
		// хранилище недоступно — клик всё равно уйдёт на шлюз
	}
};

const post = (events: AdEvent[]): void => {
	const id = visitor();
	if (!GATEWAY_URL || !id) return;
	const token = getBuyerAuthToken();
	void fetch(`${GATEWAY_URL.replace(/\/$/, "")}/ads/events`, {
		method: "POST",
		keepalive: true,
		headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
		body: JSON.stringify({ visitor: id, events }),
	}).catch(() => {});
};

const flush = (): void => {
	if (flushTimer) {
		clearTimeout(flushTimer);
		flushTimer = null;
	}
	if (queue.length === 0) return;
	const events = queue;
	queue = [];
	post(events);
};

const bindLifecycle = (): void => {
	if (listenersBound || !isBrowser()) return;
	listenersBound = true;
	document.addEventListener("visibilitychange", () => {
		if (document.visibilityState === "hidden") flush();
	});
	window.addEventListener("pagehide", flush);
};

const enqueue = (event: AdEvent): void => {
	if (!isBrowser() || !GATEWAY_URL) return;
	bindLifecycle();
	queue.push(event);
	if (queue.length >= BATCH_SIZE) {
		flush();
		return;
	}
	if (flushTimer) clearTimeout(flushTimer);
	flushTimer = setTimeout(flush, FLUSH_DELAY_MS);
};

export const trackAdImpression = (slot: AdSlot, productId: string): void => {
	const key = `${slot.campaignId}:${productId}`;
	if (seenSet().has(key)) return;
	rememberSeen(key);
	enqueue({ campaignId: slot.campaignId, productId, kind: "impression", placement: slot.placement });
};

/** Клик уходит сразу: за ним обычно переход на другую страницу. */
export const trackAdClick = (slot: AdSlot, productId: string): void => {
	rememberClick({ campaignId: slot.campaignId, productId });
	queue.push({ campaignId: slot.campaignId, productId, kind: "click", placement: slot.placement });
	flush();
};

/**
 * После входа — привязать клики устройства к аккаунту. Один раз на токен:
 * пустая пачка с токеном, шлюз проставляет user_id кликам этого устройства.
 */
export const syncAdIdentity = (): void => {
	if (!isBrowser() || !GATEWAY_URL) return;
	const token = getBuyerAuthToken();
	if (!token || storedClicks().length === 0) return;
	const mark = token.slice(-12);
	try {
		if (window.sessionStorage.getItem(SYNCED_KEY) === mark) return;
		window.sessionStorage.setItem(SYNCED_KEY, mark);
	} catch {
		// без хранилища — синхронизируем на каждом открытии, это дёшево
	}
	post([]);
};

if (isBrowser()) {
	// Вход на витрине — смена токена в профиле.
	try {
		useProfile.subscribe((state, prev) => {
			if (state.token && state.token !== prev.token) syncAdIdentity();
		});
	} catch {
		// стор ещё не готов — синхронизация пройдёт при следующем показе рекламы
	}
}

// ── Наблюдатель показов ────────────────────────────────────────────────────

type Watched = { slot: AdSlot; productId: string; timer: ReturnType<typeof setTimeout> | null };
const watched = new WeakMap<Element, Watched>();
let observer: IntersectionObserver | null = null;

const adObserver = (): IntersectionObserver | null => {
	if (!isBrowser() || typeof IntersectionObserver === "undefined") return null;
	if (observer) return observer;
	observer = new IntersectionObserver(
		(entries) => {
			for (const entry of entries) {
				const state = watched.get(entry.target);
				if (!state) continue;
				if (entry.isIntersecting) {
					if (state.timer) continue;
					state.timer = setTimeout(() => {
						state.timer = null;
						trackAdImpression(state.slot, state.productId);
						observer?.unobserve(entry.target);
						watched.delete(entry.target);
					}, IMPRESSION_DWELL_MS);
				} else if (state.timer) {
					clearTimeout(state.timer);
					state.timer = null;
				}
			}
		},
		{ threshold: IMPRESSION_RATIO },
	);
	return observer;
};

/** Ref рекламной карточки: показ по появлению в окне просмотра. Без рекламы — пустышка. */
export const useAdImpressionRef = (slot: AdSlot | undefined, productId: string) => {
	const campaignId = slot?.campaignId;
	const placement = slot?.placement;
	return useCallback(
		(element: HTMLElement | null) => {
			if (!campaignId || !placement || !element || !productId) return;
			const io = adObserver();
			if (!io || seenSet().has(`${campaignId}:${productId}`)) return;
			watched.set(element, { slot: { campaignId, placement }, productId, timer: null });
			io.observe(element);
			return () => {
				const state = watched.get(element);
				if (state?.timer) clearTimeout(state.timer);
				watched.delete(element);
				io.unobserve(element);
			};
		},
		[campaignId, placement, productId],
	);
};

// ── Рекламные позиции ──────────────────────────────────────────────────────

export type AdPlacementQuery = {
	place: AdPlacement;
	categoryId?: string | null;
	city?: string | null;
	limit?: number;
	/** Товары, которые рекламой не показывать (например, открытый сейчас товар). */
	exclude?: string[];
};

export const fetchAdPlacements = async (query: AdPlacementQuery): Promise<AdItem[]> => {
	if (!GATEWAY_URL) return [];
	const params = new URLSearchParams({ place: query.place });
	if (query.categoryId) params.set("categoryId", query.categoryId);
	if (query.city) params.set("city", query.city);
	if (query.limit) params.set("limit", String(query.limit));
	if (query.exclude?.length) params.set("exclude", query.exclude.join(","));
	const res = await fetch(`${GATEWAY_URL.replace(/\/$/, "")}/ads/placements?${params}`);
	if (!res.ok) return [];
	const payload = (await res.json().catch(() => null)) as { success?: boolean; data?: unknown[] } | null;
	if (!payload?.success || !Array.isArray(payload.data)) return [];
	const items: AdItem[] = [];
	for (const raw of payload.data as (Omit<AdItem, "product"> & { product: unknown })[]) {
		const product = Product.safeParse(raw.product);
		if (product.success) items.push({ ...raw, product: product.data });
	}
	return items;
};

/** Реклама под место показа. Ошибка или пустой ответ — просто без рекламы. */
export const useAdPlacements = (query: AdPlacementQuery, enabled = true) =>
	useQuery({
		queryKey: [
			"ads",
			"placements",
			query.place,
			query.categoryId ?? "",
			query.city ?? "",
			query.limit ?? 0,
			(query.exclude ?? []).join(","),
		],
		queryFn: async () => {
			syncAdIdentity();
			return fetchAdPlacements(query).catch(() => []);
		},
		enabled: enabled && Boolean(GATEWAY_URL),
		staleTime: 60_000,
	});

/** Товары выдачи без тех, что уже стоят в рекламе: карточка не повторяется. */
export const withoutAdProducts = <T extends { id: string }>(organic: T[], ads: AdItem[]): T[] => {
	const adIds = new Set(ads.map((a) => a.productId));
	return organic.filter((p) => !adIds.has(p.id));
};

/**
 * Подмес рекламы в выдачу: рекламные позиции встают на места 1, 5, 9 …
 * (каждая четвёртая карточка, начиная с первой), органические идут
 * следом в своём порядке.
 */
export const mixAds = <T>(
	organic: T[],
	ads: AdItem[],
	every = 4,
): ({ kind: "organic"; item: T } | { kind: "ad"; ad: AdItem })[] => {
	const out: ({ kind: "organic"; item: T } | { kind: "ad"; ad: AdItem })[] = [];
	let adIndex = 0;
	let organicIndex = 0;
	for (let slot = 0; ; slot += 1) {
		const ad = ads[adIndex];
		// Реклама встаёт, только пока за ней есть органика: хвост из одной
		// рекламы подряд выглядел бы как рекламный блок, а не как выдача.
		if (slot % every === 0 && ad && organicIndex < organic.length) {
			out.push({ kind: "ad", ad });
			adIndex += 1;
			continue;
		}
		if (organicIndex >= organic.length) break;
		out.push({ kind: "organic", item: organic[organicIndex] as T });
		organicIndex += 1;
	}
	return out;
};
